import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import InnerLayout from '../../components/layouts/innerLayout';
import InputField from '../../components/forms/InputField';
import { accountDetailsAction, accountUpdateAction, uploadImageAction } from '../../redux/actions/authAction';
import { errorToast, successToast } from '../../components/toast';
import LoaderModal from '../../components/modals/loaderModal';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import RadioField from '../../components/forms/radioInput';


const AccountDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [enableGST, setEnableGST] = useState(false);
    const [accountData, setAccountData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const imageInputRef = useRef(null)

    const initialValues = {
        name: accountData?.name || "",
        mobile: accountData?.mobile || "",
        email: accountData?.email || "",
        address: accountData?.address || "",
        state: accountData?.state || "",
        city: accountData?.city || "",
        pincode: accountData?.pincode || "",
        logo: accountData?.logo || null,
        gstEnabled: accountData?.gstEnabled,
        default_tax: accountData?.default_tax || '',
        gst_number: accountData?.gst_number || '',
        tax_with_product: accountData?.tax_with_product || null,
        transaction_in: accountData?.transaction_in || "intraState",
        printPreference: accountData?.printPreference || "A4",
        fssaiNumber: accountData?.fssaiNumber || "",
        business_type: accountData?.business_type || "",
    };

    // Validation Schema
    const validationSchema = Yup.object({
        name: Yup.string().required('Store name is required'),
        mobile: Yup.string()
            .required('mobile is required')
            .matches(/^[0-9]{10}$/, 'mobile must be a 10-digit number'),
        email: Yup.string().email('Invalid email').required('Email is required'),
        address: Yup.string().required('Address is required'),
        state: Yup.string().required('State is required'),
        pincode: Yup.string()
            .required('Pincode is required')
            .matches(/^[0-9]{6}$/, 'Pincode must be a 6-digit number'),
        business_type: Yup.string().required('Business type is required'),
        default_tax: enableGST ? Yup.string().required('Required!') : "",
        gst_number: enableGST ? Yup.string().required('Required!') : "",
    });

    const handleFormSubmit = (values) => {
        console.log('Form values:', values);
        setLoading(true)
        dispatch(accountUpdateAction(values, onSuccess, onError))
    };


    useEffect(() => {
        fetchAccount()
    }, [])

    const handleImageChange = (event, setFieldValue) => {
        const imageUploadSuccess = (data) => {
            console.log(data.data, "success data");
            setImagePreview(data.data.data);
            setFieldValue('logo', data.data.data)
            setLoading(false)
        }
        const file = event.target.files[0];
        setLoading(true)
        dispatch(uploadImageAction(file, imageUploadSuccess, onError));
    };

    const handleRemoveImage = (setFieldValue) => {
        setImagePreview(null);
        setFieldValue("logo", null);
        // Clear the input field value manually using the ref
        if (imageInputRef.current) {
            imageInputRef.current.value = null;
        }
    };


    const fetchAccount = () => {
        setLoading(true)
        dispatch(accountDetailsAction({}, onSuccess, onError))
    }

    const onSuccess = (data) => {
        // successToast(data.data.message)
        setAccountData(data.data.data);
        setEnableGST(data.data.data.gstEnabled);
        setImagePreview(data.data.data.logo)
        setLoading(false);
        setIsEditing(false);
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
        setLoading(false)
    }

    const isOfferEndingSoon = () => {
        if (!accountData?.offerEndDate) return false;

        const endDate = dayjs(accountData.offerEndDate);
        const today = dayjs();
        return endDate.diff(today, 'day') <= 5;
    };

    return (
        <InnerLayout module="Account">
            <div className="h-full flex flex-col bg-gray-100 p-4">
                {/* Static Header */}
                <div className="flex justify-between items-center p-4 bg-white border-b shadow-sm">
                    <h1 className="text-lg font-bold">Account Details</h1>
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className={`px-4 py-2 rounded ${isEditing ? 'bg-gray-500 text-white' : 'bg-blue-500 text-white'}`}
                    >
                        {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-auto h-full bg-white">
                    {isOfferEndingSoon() && (
                        <div className={`p-2 sticky top-0 z-10 flex justify-between items-center rounded shadow-md ${dayjs(accountData.offerEndDate).isBefore(dayjs(), 'day') ? 'bg-red-100 border-l-4 border-red-500 text-red-700' : 'bg-yellow-50 border-l-4 border-yellow-600 text-yellow-800'}`}>
                            {dayjs(accountData.offerEndDate).isBefore(dayjs(), 'day') ? (
                                <p className="text-start">
                                    Your subscription has <span className="font-bold">expired</span>.
                                    Please renew your subscription to continue enjoying uninterrupted service.
                                </p>
                            ) : (
                                <p className="text-start">
                                    Your subscription will end on{" "}
                                    <span className="font-bold">
                                        {dayjs(accountData.offerEndDate).format('DD MMMM YYYY')}
                                    </span>.
                                    Renew now to ensure uninterrupted service. The newly purchased subscription will automatically activate after the current one expires.
                                </p>
                            )}
                            <button
                                className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded transition duration-200"
                                onClick={() => navigate('/subscribe')}
                            >
                                Renew Now
                            </button>
                        </div>
                    )}

                    <div className="p-4 h-full">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleFormSubmit}
                            enableReinitialize
                        >
                            {({ setFieldValue, values, }) => (
                                <Form id="accountForm" className="flex flex-wrap md-lg:flex-nowrap md:px-28 text-start px-4">
                                    <div className='w-full px-4'>
                                        {/* Store Details */}
                                        <div style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.name}
                                                label="Store Name"
                                                name="name"
                                                type="text"
                                                placeholder="Enter store name"
                                                disabled={!isEditing}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>

                                        <div style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.mobile}
                                                label="Contact"
                                                name="mobile"
                                                type="text"
                                                placeholder="Enter mobile number"
                                                disabled={!isEditing}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>
                                        <div style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.email}
                                                label="Email"
                                                name="email"
                                                type="email"
                                                placeholder="Enter email address"
                                                disabled={true}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>
                                        <div style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.address}
                                                label="Address"
                                                name="address"
                                                type="text"
                                                placeholder="Enter address"
                                                disabled={!isEditing}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>
                                        <div style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.state}
                                                label="State"
                                                name="state"
                                                type="text"
                                                placeholder="Enter state"
                                                disabled={!isEditing}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>
                                        <div style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.city}
                                                label="City"
                                                name="city"
                                                type="text"
                                                placeholder="Enter city"
                                                disabled={!isEditing}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>
                                        <div style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.pincode}
                                                label="Pincode"
                                                name="pincode"
                                                type="text"
                                                placeholder="Enter pincode"
                                                disabled={!isEditing}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>
                                    </div>
                                    <div className='w-full px-4'>
                                        {/* Logo Upload */}
                                        <div>
                                            <label className="block  mb-1">Logo</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id="logo"
                                                disabled={!isEditing}
                                                onChange={(e) => handleImageChange(e, setFieldValue)}
                                                className="w-full border px-3 py-2 rounded max-w-[25rem]"
                                                ref={imageInputRef}
                                            />
                                            {imagePreview && (
                                                <div className="mt-2 flex gap-3 items-start mx-2">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Logo Preview"
                                                        className="h-20 w-20 object-cover rounded"
                                                    />
                                                    {isEditing &&
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveImage(setFieldValue)} // Call the remove function
                                                            className="mt-2 text-red-500 underline text-sm"
                                                        >
                                                            Remove
                                                        </button>
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96 pt-3">
                                            <RadioField
                                                label="Business Type"
                                                name="business_type"
                                                options={[
                                                    { label: "Restaurant", value: "restaurant" },
                                                    { label: "General", value: "general" },
                                                ]}
                                                direction="row" // or "col" for vertical layout
                                                disabled={!isEditing}
                                            />
                                        </div>

                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96 pt-3">
                                            <RadioField
                                                label="Print Preference"
                                                name="printPreference"
                                                options={[
                                                    { label: "A4", value: "A4" },
                                                    { label: "Landscape", value: "landscape" },
                                                    { label: "Thermal Print", value: "thermal" },
                                                ]}
                                                direction="row" // or "col" for vertical layout
                                                disabled={!isEditing}
                                            />
                                        </div>

                                        <div className='pt-3' style={{ width: "100%", maxWidth: "25rem" }}>
                                            <InputField
                                                value={values.fssaiNumber}
                                                label="FSSAI No"
                                                name="fssaiNumber"
                                                type="text"
                                                placeholder="Enter Fssai Lic No"
                                                disabled={!isEditing}
                                                style={{ width: "100%", maxWidth: "25rem" }}
                                            />
                                        </div>

                                        {/* Enable GST */}
                                        <div>
                                            <h2 className="text-lg font-bold mt-6">Tax Settings</h2>
                                            <div className="flex items-center gap-4">
                                                <label className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={enableGST}
                                                        disabled={!isEditing}
                                                        onChange={(e) => {
                                                            setEnableGST(e.target.checked);
                                                            setFieldValue('gstEnabled', e.target.checked);

                                                        }}
                                                        className="mr-2"
                                                    />
                                                    Enable GST
                                                </label>
                                            </div>
                                        </div>

                                        {/* Tax Settings (Visible Only if GST is Enabled) */}
                                        {enableGST && (
                                            <>
                                                <div style={{ width: "100%", maxWidth: "25rem" }}>
                                                    <InputField
                                                        value={values.default_tax}
                                                        label="Default Tax Rate (%)"
                                                        name="default_tax"
                                                        type="number"
                                                        placeholder="Enter default tax rate"
                                                        disabled={!isEditing}
                                                        style={{ width: "100%", maxWidth: "25rem" }}
                                                    />
                                                </div>
                                                <div style={{ width: "100%", maxWidth: "25rem" }}>
                                                    <InputField
                                                        value={values.gst_number}
                                                        label="GST Number"
                                                        name="gst_number"
                                                        type="text"
                                                        placeholder="Enter GST number"
                                                        disabled={!isEditing}
                                                        style={{ width: "100%", maxWidth: "25rem" }}
                                                    />
                                                </div>
                                                <div>
                                                    <RadioField
                                                        label="Tax With Product"
                                                        name="tax_with_product"
                                                        options={[
                                                            { label: "Inclusive Tax", value: "inclusive" },
                                                            { label: "Exclusive Tax", value: "exclusive" },
                                                        ]}
                                                        direction="row" // or "col" for vertical layout
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                                {/* <div className='mb-3'>                                                    
                                                    <RadioField
                                                        label="Transaction Type"
                                                        name="transaction_in"
                                                        options={[
                                                            { label: "Intra-State", value: "intraState" },
                                                            { label: "Inter-State", value: "interState" },
                                                        ]}
                                                        direction="row" // or "col" for vertical layout
                                                        disabled={!isEditing}
                                                    />
                                                </div> */}
                                            </>
                                        )}
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>


                <div className="p-4 bg-white border-t flex gap-8 justify-end">
                    <>
                        <button
                            onClick={() => setIsEditing(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            disabled={!isEditing}
                            type="submit"
                            form="accountForm"
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                        >
                            Save
                        </button>
                    </>
                </div>
                {loading &&
                    <LoaderModal />
                }
            </div>
        </InnerLayout>
    );
};

export default AccountDetails;
